/**
 * Client for governed source viewer session issue/resolve endpoints.
 * The browser never supplies authoritative document/page/passage coordinates.
 */

import {
  COGNITORY_V2_GOVERNED_SOURCE_VIEW_SESSION_PATH,
  resolveCognitoryApiBaseUrl,
} from "./cognitoryApiBase";
import { resolveCognitoryBearerToken } from "./cognitoryBearerToken";
import type { CognitoryCitationIdentity } from "../presentation/cognitoryCitationPresentation";

export type GovernedSourceViewSessionIssueResult =
  | {
      kind: "issued";
      requestId: string;
      governedSession: string;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "not_found"
        | "temporary_failure"
        | "invalid";
      requestId: string | null;
    };

export type GovernedSourceViewSessionResolveResult =
  | {
      kind: "resolved";
      requestId: string;
      citationNumber: string;
      documentId: string;
      documentVersionId: string;
      documentTitle: string;
      citedPassage: string;
      pageNumber: number;
    }
  | {
      kind:
        | "unauthenticated"
        | "forbidden"
        | "not_found"
        | "temporary_failure"
        | "invalid";
      requestId: string | null;
    };

function requiredString(value: unknown): string | null {
  return typeof value === "string" && value.length > 0 ? value : null;
}

function parseRequestId(body: unknown): string | null {
  return body &&
    typeof body === "object" &&
    typeof (body as { requestId?: unknown }).requestId === "string"
    ? (body as { requestId: string }).requestId
    : null;
}

export async function issueGovernedSourceViewSession(input: {
  cognitoryId: string;
  citation: Pick<CognitoryCitationIdentity, "verificationReference">;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<GovernedSourceViewSessionIssueResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const verificationReference = requiredString(
    input.citation.verificationReference,
  );
  if (!verificationReference) {
    return { kind: "invalid", requestId: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  let response: Response;
  try {
    response = await fetchImpl(
      `${base}${COGNITORY_V2_GOVERNED_SOURCE_VIEW_SESSION_PATH}`,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        cache: "no-store",
        body: JSON.stringify({
          cognitoryId: input.cognitoryId,
          verificationReference,
        }),
      },
    );
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId = parseRequestId(body);

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 404) {
    return { kind: "not_found", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }

  if (!body || typeof body !== "object") {
    return { kind: "invalid", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision === "session_issued") {
    const governedSession = requiredString(
      (body as { governedSession?: unknown }).governedSession,
    );
    if (!governedSession) {
      return { kind: "invalid", requestId };
    }
    return {
      kind: "issued",
      requestId: requestId ?? "unknown",
      governedSession,
    };
  }

  if (
    decision === "temporary_failure" ||
    decision === "cognitory_unavailable"
  ) {
    return { kind: "temporary_failure", requestId };
  }
  if (decision === "forbidden" || decision === "unauthenticated") {
    return {
      kind: decision === "forbidden" ? "forbidden" : "unauthenticated",
      requestId,
    };
  }
  if (decision === "source_not_found" || decision === "session_not_found") {
    return { kind: "not_found", requestId };
  }

  return { kind: "invalid", requestId };
}

export async function resolveGovernedSourceViewSession(input: {
  cognitoryId: string;
  governedSession: string;
  apiBaseUrl?: string;
  bearerToken?: string | null;
  fetchImpl?: typeof fetch;
}): Promise<GovernedSourceViewSessionResolveResult> {
  const fetchImpl = input.fetchImpl ?? fetch;
  const token =
    input.bearerToken !== undefined
      ? input.bearerToken
      : await resolveCognitoryBearerToken();
  if (!token) {
    return { kind: "unauthenticated", requestId: null };
  }

  const governedSession = requiredString(input.governedSession);
  if (!governedSession) {
    return { kind: "invalid", requestId: null };
  }

  const base = input.apiBaseUrl ?? resolveCognitoryApiBaseUrl();
  const params = new URLSearchParams({
    cognitoryId: input.cognitoryId,
    governedSession,
  });
  let response: Response;
  try {
    response = await fetchImpl(
      `${base}${COGNITORY_V2_GOVERNED_SOURCE_VIEW_SESSION_PATH}?${params.toString()}`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "application/json",
        },
        cache: "no-store",
      },
    );
  } catch {
    return { kind: "temporary_failure", requestId: null };
  }

  let body: unknown = null;
  try {
    body = await response.json();
  } catch {
    body = null;
  }

  const requestId = parseRequestId(body);

  if (response.status === 401) {
    return { kind: "unauthenticated", requestId };
  }
  if (response.status === 403) {
    return { kind: "forbidden", requestId };
  }
  if (response.status === 404) {
    return { kind: "not_found", requestId };
  }
  if (response.status === 503) {
    return { kind: "temporary_failure", requestId };
  }

  if (!body || typeof body !== "object") {
    return { kind: "invalid", requestId };
  }

  const decision = (body as { decision?: unknown }).decision;
  if (decision === "session_resolved") {
    const citationNumber = requiredString(
      (body as { citationNumber?: unknown }).citationNumber,
    );
    const documentId = requiredString(
      (body as { documentId?: unknown }).documentId,
    );
    const documentVersionId = requiredString(
      (body as { documentVersionId?: unknown }).documentVersionId,
    );
    const documentTitle = requiredString(
      (body as { documentTitle?: unknown }).documentTitle,
    );
    const citedPassage = requiredString(
      (body as { citedPassage?: unknown }).citedPassage,
    );
    const pageNumberRaw = (body as { pageNumber?: unknown }).pageNumber;
    const pageNumber =
      typeof pageNumberRaw === "number" &&
      Number.isInteger(pageNumberRaw) &&
      pageNumberRaw > 0
        ? pageNumberRaw
        : null;
    if (
      !citationNumber ||
      !documentId ||
      !documentVersionId ||
      !documentTitle ||
      !citedPassage ||
      pageNumber === null
    ) {
      return { kind: "invalid", requestId };
    }
    return {
      kind: "resolved",
      requestId: requestId ?? "unknown",
      citationNumber,
      documentId,
      documentVersionId,
      documentTitle,
      citedPassage,
      pageNumber,
    };
  }

  if (
    decision === "temporary_failure" ||
    decision === "cognitory_unavailable"
  ) {
    return { kind: "temporary_failure", requestId };
  }
  if (decision === "forbidden" || decision === "unauthenticated") {
    return {
      kind: decision === "forbidden" ? "forbidden" : "unauthenticated",
      requestId,
    };
  }
  if (decision === "session_not_found" || decision === "source_not_found") {
    return { kind: "not_found", requestId };
  }

  return { kind: "invalid", requestId };
}
